import { DocsunVitalsSDK as CoreSDK } from '@docsun/vitals-core';
import { CameraController } from './camera';
import type {
  BrowserVitalsOptions,
  VitalsCoreLike,
  VitalsEventMap,
  VitalResult,
  IndicatorsPayload
} from './types';

export class BrowserVitalsSDK {
  private readonly core: VitalsCoreLike;
  private readonly options: BrowserVitalsOptions;
  private readonly camera: CameraController;
  private stream: MediaStream | null = null;
  private initializing = false;

  constructor(options: BrowserVitalsOptions) {
    if (!options.apiToken) throw new Error('apiToken is required');
    if (!options.licenceKey) throw new Error('licenceKey is required');

    this.options = {
      userAgent:
        options.userAgent ||
        (typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'),
      ...options
    };

    this.camera = new CameraController(options.camera);

    // Instantiate the core. You only pass through the relevant options.
    this.core = new (CoreSDK as unknown as { new (opts: any): VitalsCoreLike })({
      apiToken: this.options.apiToken,
      licenceKey: this.options.licenceKey,
      licenceType: this.options.licenceType,
      deviceId: this.options.deviceId,
      deviceToken: this.options.deviceToken,
      userAgent: this.options.userAgent,
      modules: this.options.modules,
      units: this.options.units,
      overlayTarget: this.options.overlayTarget,
      drawFaceBox: this.options.drawFaceBox,
      drawFaceLandmarks: this.options.drawFaceLandmarks
    });

    if (typeof window !== 'undefined') {
      (window as any).DocsunVitalsCoreInstance = this.core;
    }
  }

  async initialize(): Promise<void> {
    const already =
      (typeof (this.core as any).isInitialized === 'boolean' &&
        (this.core as any).isInitialized) ||
      (typeof (this.core as any).isReady === 'boolean' &&
        (this.core as any).isReady);
    if (already) return;
    if (this.initializing) return;
    this.initializing = true;
    try {
      await this.core.initialize();
    } finally {
      this.initializing = false;
    }
  }

  async startScan(stream?: MediaStream): Promise<void> {
    await this.initialize();
    if (!stream) {
      this.stream = await this.camera.start();
      stream = this.stream;
    } else {
      this.stream = stream;
    }
    await this.core.startScan(stream);
  }

  async stopScan(): Promise<void> {
    try {
      await this.core.stopScan();
    } finally {
      this.camera.stop();
      this.stream = null;
    }
  }

  on<EventName extends keyof VitalsEventMap>(
    event: EventName,
    handler: (payload: VitalsEventMap[EventName]) => void
  ): this {
    this.core.on(event, handler as any);
    return this;
  }

  off<EventName extends keyof VitalsEventMap>(
    event: EventName,
    handler: (payload: VitalsEventMap[EventName]) => void
  ): this {
    if (typeof (this.core as any).off === 'function') {
      (this.core as any).off(event, handler as any);
    }
    return this;
  }

  onVitalResult(handler: (result: VitalResult) => void): this {
    return this.on('vital-result', handler);
  }

  onIndicatorsResult(handler: (ind: IndicatorsPayload) => void): this {
    return this.on('indicators-result', handler);
  }

  getCurrentStream(): MediaStream | null {
    return this.stream;
  }

  getCore(): VitalsCoreLike {
    return this.core;
  }
}
